import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Subject, timer } from 'rxjs';
import { filter, map, race, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';

interface DialSymbol {
  dialNumber: string;
  dialAlphabet: string;
  longPressSupport?: boolean;
}
@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-call-as-dial',
  templateUrl: './call-as-dial.component.html',
  styleUrls: ['./call-as-dial.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallAsDialComponent implements OnInit {
  @Output()
  public dialButtonClick: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public dialButtonLongPress: EventEmitter<string> = new EventEmitter<string>();

  @Input()
  public isDisabled: boolean = false;

  public icons = ICONS;

  public translations = TranslationKeys;

  private readonly holdInterval = 1000;

  private readonly up$: Subject<void> = new Subject<void>();
  private readonly down$: Subject<DialSymbol> = new Subject<DialSymbol>();

  public data: DialSymbol[] = [
    {
      dialNumber: '1',
      dialAlphabet: '',
    },
    {
      dialNumber: '2',
      dialAlphabet: 'abc',
    },
    {
      dialNumber: '3',
      dialAlphabet: 'def',
    },
    {
      dialNumber: '4',
      dialAlphabet: 'ghi',
    },
    {
      dialNumber: '5',
      dialAlphabet: 'jkl',
    },
    {
      dialNumber: '6',
      dialAlphabet: 'mno',
    },
    {
      dialNumber: '7',
      dialAlphabet: 'pqrs',
    },
    {
      dialNumber: '8',
      dialAlphabet: 'tuv',
    },
    {
      dialNumber: '9',
      dialAlphabet: 'wxyz',
    },
    {
      dialNumber: '*',
      dialAlphabet: '',
    },
    {
      dialNumber: '0',
      dialAlphabet: '+',
      longPressSupport: true,
    },
    {
      dialNumber: '#',
      dialAlphabet: '',
    },
  ];

  public ngOnInit(): void {
    this.down$
      .pipe(
        switchMap((symbol) =>
          this.up$.pipe(
            map(() => ({ symbol, type: 'click' })),
            race(
              timer(this.holdInterval).pipe(
                filter(() => !!symbol.longPressSupport),
                map(() => ({ symbol, type: 'hold' })),
              ),
            ),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        if (value.type === 'hold') {
          this.dialButtonLongPress.emit(value.symbol.dialAlphabet);
        }
      });
  }

  public onDialButtonDown(item: DialSymbol): void {
    if (!this.isDisabled) {
      this.dialButtonClick.emit(item.dialNumber);
      this.down$.next(item);
    }
  }

  public onDialButtonUp(): void {
    if (!this.isDisabled) {
      this.up$.next();
    }
  }
}
